import { useState } from 'react';
import { LayoutGrid, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/Popover';
import {
  useDashboardPreferencesActions,
  useDashboardPreferencesState,
} from '../../hooks/use-dashboard-preferences';
import { MAX_VIEWS } from '../../types/dashboard-preferences';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export function ViewManager() {
  const { preferences, activeView } = useDashboardPreferencesState();
  const { switchView, createView, deleteView, renameView } = useDashboardPreferencesActions();

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const startRename = (viewId: string, currentName: string) => {
    setEditingId(viewId);
    setEditName(currentName);
  };

  const confirmRename = () => {
    if (editingId && editName.trim()) {
      renameView(editingId, editName.trim());
    }
    setEditingId(null);
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    const created = createView(newName.trim());
    if (created) {
      toast.success(`View "${created.name}" created`);
    }
    setCreating(false);
    setNewName('');
  };

  const handleDelete = (viewId: string, viewName: string) => {
    if (preferences.views.length <= 1) {
      toast.error('Cannot delete the last view');
      return;
    }
    deleteView(viewId);
    toast.success(`View "${viewName}" deleted`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg border border-charcoalGray-50 px-3 py-2 text-sm text-charcoalGray-700 transition-colors hover:bg-charcoalGray-50 dark:border-charcoalGray-500 dark:text-charcoalGray-100 dark:hover:bg-charcoalGray-600"
        >
          <LayoutGrid className="size-4" />
          <span className="max-w-[120px] truncate">{activeView.name}</span>
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-72 p-0">
        <div className="border-b border-charcoalGray-50 px-4 py-3 dark:border-charcoalGray-500">
          <h3 className="text-sm font-semibold text-charcoalGray-800 dark:text-white">
            Dashboard Views
          </h3>
          <p className="text-xs text-charcoalGray-400 dark:text-charcoalGray-200">
            Switch or manage views
          </p>
        </div>

        <div className="max-h-[300px] overflow-y-auto p-2">
          {preferences.views.map((view) => (
            <div
              key={view.id}
              className={cn(
                'group flex items-center gap-2 rounded-lg px-3 py-2 transition-colors',
                view.id === activeView.id
                  ? 'bg-primary-500/10'
                  : 'hover:bg-charcoalGray-50 dark:hover:bg-charcoalGray-600',
              )}
            >
              {editingId === view.id ? (
                <div className="flex flex-1 items-center gap-1">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmRename();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    className="flex-1 rounded border border-charcoalGray-100 bg-transparent px-2 py-1 text-sm text-charcoalGray-800 outline-none focus:border-primary-500 dark:border-charcoalGray-500 dark:text-white"
                    autoFocus
                  />
                  <button type="button" onClick={confirmRename} className="p-1 text-green-500">
                    <Check className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="p-1 text-charcoalGray-400"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      switchView(view.id);
                      setOpen(false);
                    }}
                    className="flex-1 text-left text-sm text-charcoalGray-800 dark:text-white"
                  >
                    {view.name}
                  </button>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => startRename(view.id, view.name)}
                      className="rounded p-1 text-charcoalGray-400 hover:text-charcoalGray-700 dark:hover:text-white"
                    >
                      <Pencil className="size-3" />
                    </button>
                    {preferences.views.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleDelete(view.id, view.name)}
                        className="rounded p-1 text-charcoalGray-400 hover:text-red-500"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Create new */}
          {creating ? (
            <div className="mt-1 flex items-center gap-1 px-3 py-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate();
                  if (e.key === 'Escape') setCreating(false);
                }}
                placeholder="View name..."
                className="flex-1 rounded border border-charcoalGray-100 bg-transparent px-2 py-1 text-sm text-charcoalGray-800 outline-none focus:border-primary-500 dark:border-charcoalGray-500 dark:text-white"
                autoFocus
              />
              <button type="button" onClick={handleCreate} className="p-1 text-green-500">
                <Check className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="p-1 text-charcoalGray-400"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            preferences.views.length < MAX_VIEWS && (
              <button
                type="button"
                onClick={() => {
                  setCreating(true);
                  setNewName('');
                }}
                className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-charcoalGray-500 hover:bg-charcoalGray-50 dark:text-charcoalGray-300 dark:hover:bg-charcoalGray-600"
              >
                <Plus className="size-4" />
                New View
              </button>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
